// components/PrivateRoute.js
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const PrivateRoute = ({ children, requireRoles = [], requireAll = false, fallback = "/unauthorized" }) => {
  const { user, isAuthLoading } = useAuth();
  const location = useLocation();

  if (isAuthLoading) {
    return <div className="text-center p-6 text-gray-500">Loading...</div>;
  }

  // must be logged in
  if (!user?.token) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  // An admin-forced password reset (see PUT /users/{id}/password) blocks
  // every other protected route until the user sets their own new
  // password -- checked here, centrally, so no individual route needs its
  // own opt-in. /change-password itself must be exempt or this redirects
  // to itself forever.
  if (user.mustResetPassword && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  // roles from auth (preferred) or fallback to localStorage
  const roles =
    user?.roles ??
    (() => {
      try {
        return JSON.parse(localStorage.getItem("roles") || "[]");
      } catch {
        return [];
      }
    })();

  // if no role requirement, allow
  if (!requireRoles.length) return children;

  const hasRequired = requireAll
    ? requireRoles.every((r) => roles.includes(r))
    : requireRoles.some((r) => roles.includes(r));

  if (!hasRequired) {
    // optional: preserve where they tried to go
    const to = `${fallback}?from=${encodeURIComponent(location.pathname)}`;
    return <Navigate to={to} replace />;
  }

  return children;
};

export default PrivateRoute;
